/**
 * ExportCenter Component (TASK-2026-062)
 *
 * Provides UI for exporting simulator data to various formats:
 * - PowerPoint (PPTX) - Executive presentations
 * - Excel (XLSX) - Raw data and analysis tables
 * - PDF - Print-ready reports
 */

import { useState, useCallback } from 'react';
import { exportToExcel, type ExcelExportData } from '@/utils/excelExport';
import { exportToPowerPoint, type PPTExportData } from '@/utils/pptExport';
import { exportToPDF } from '@/utils/pdfExport';

export type ExportFormat = 'pptx' | 'xlsx' | 'pdf';

export interface ExportCenterProps {
  clientName: string;
  data: {
    market?: ExcelExportData['market'];
    portfolio?: ExcelExportData['portfolio'];
    pricing?: ExcelExportData['pricing'];
    opportunities?: ExcelExportData['opportunities'];
    insights?: PPTExportData['insights'];
  };
  pdfElementId?: string;
  onExportStart?: (format: ExportFormat) => void;
  onExportComplete?: (format: ExportFormat) => void;
  onExportError?: (format: ExportFormat, error: Error) => void;
}

interface FormatOption {
  id: ExportFormat;
  name: string;
  description: string;
  icon: React.ReactNode;
}

const FORMAT_OPTIONS: FormatOption[] = [
  {
    id: 'pptx',
    name: 'PowerPoint',
    description: 'Executive presentation with KPIs, charts, and insights',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5M9 11.25v1.5M12 9v3.75m3-6v6" />
      </svg>
    ),
  },
  {
    id: 'xlsx',
    name: 'Excel',
    description: 'Raw data export with multiple sheets for analysis',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.375 19.5h17.25m-17.25 0a1.125 1.125 0 01-1.125-1.125M3.375 19.5h7.5c.621 0 1.125-.504 1.125-1.125m-9.75 0V5.625m0 12.75v-1.5c0-.621.504-1.125 1.125-1.125m18.375 2.625V5.625m0 12.75c0 .621-.504 1.125-1.125 1.125m1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125m0 3.75h-7.5A1.125 1.125 0 0112 18.375m9.75-12.75c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125m19.5 0v1.5c0 .621-.504 1.125-1.125 1.125M2.25 5.625v1.5c0 .621.504 1.125 1.125 1.125m0 0h17.25m-17.25 0h7.5c.621 0 1.125.504 1.125 1.125M3.375 8.25c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125m17.25-3.75h-7.5c-.621 0-1.125.504-1.125 1.125m8.625-1.125c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125" />
      </svg>
    ),
  },
  {
    id: 'pdf',
    name: 'PDF',
    description: 'Print-ready summary document',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
      </svg>
    ),
  },
];

export function ExportCenter({
  clientName,
  data,
  pdfElementId = 'export-content',
  onExportStart,
  onExportComplete,
  onExportError,
}: ExportCenterProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportingFormat, setExportingFormat] = useState<ExportFormat | null>(null);

  const handleExport = useCallback(
    async (format: ExportFormat) => {
      setIsExporting(true);
      setExportingFormat(format);
      onExportStart?.(format);

      try {
        const generatedAt = new Date();

        switch (format) {
          case 'xlsx':
            exportToExcel({
              clientName,
              generatedAt,
              market: data.market,
              portfolio: data.portfolio,
              pricing: data.pricing,
              opportunities: data.opportunities,
            });
            break;

          case 'pptx':
            exportToPowerPoint({
              clientName,
              generatedAt,
              market: data.market,
              portfolio: data.portfolio,
              opportunities: data.opportunities,
              insights: data.insights,
            });
            break;

          case 'pdf':
            exportToPDF(pdfElementId, {
              title: `${clientName} - Executive Summary`,
              filename: `${clientName.toLowerCase().replace(/\s+/g, '-')}-summary`,
            });
            break;
        }

        onExportComplete?.(format);
      } catch (error) {
        console.error(`Export to ${format} failed:`, error);
        onExportError?.(format, error as Error);
      } finally {
        setIsExporting(false);
        setExportingFormat(null);
      }
    },
    [clientName, data, pdfElementId, onExportStart, onExportComplete, onExportError]
  );

  return (
    <div className="relative">
      {/* Export Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-500 text-white rounded-lg transition-colors"
        disabled={isExporting}
      >
        {isExporting ? (
          <>
            <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <span>Exporting...</span>
          </>
        ) : (
          <>
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            <span>Export</span>
          </>
        )}
      </button>

      {/* Dropdown Menu */}
      {isOpen && !isExporting && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />

          {/* Menu */}
          <div className="absolute right-0 mt-2 w-72 bg-surface-700 border border-surface-600 rounded-lg shadow-xl z-20">
            <div className="p-3 border-b border-surface-600">
              <h3 className="text-sm font-semibold text-white">Export Report</h3>
              <p className="text-xs text-text-secondary mt-1">Choose a format to download</p>
            </div>

            <div className="p-2">
              {FORMAT_OPTIONS.map((option) => (
                <button
                  key={option.id}
                  onClick={() => {
                    setIsOpen(false);
                    handleExport(option.id);
                  }}
                  className="w-full flex items-start gap-3 p-3 rounded-lg hover:bg-surface-600 transition-colors text-left"
                >
                  <div className="text-primary-400 mt-0.5">{option.icon}</div>
                  <div>
                    <div className="text-sm font-medium text-white">{option.name}</div>
                    <div className="text-xs text-text-secondary mt-0.5">{option.description}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Export Progress Overlay */}
      {isExporting && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-surface-700 rounded-lg p-6 shadow-xl max-w-sm w-full mx-4">
            <div className="flex items-center gap-4">
              <div className="animate-spin w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full" />
              <div>
                <h3 className="text-white font-medium">
                  Generating {exportingFormat?.toUpperCase()}
                </h3>
                <p className="text-sm text-text-secondary">
                  Please wait while your file is being prepared...
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Compact export button for tight spaces
 */
export function ExportButton({
  format,
  onClick,
  disabled,
}: {
  format: ExportFormat;
  onClick: () => void;
  disabled?: boolean;
}) {
  const option = FORMAT_OPTIONS.find((o) => o.id === format);
  if (!option) return null;

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="flex items-center gap-2 px-3 py-1.5 text-sm bg-surface-600 hover:bg-surface-500 text-text-secondary hover:text-white rounded transition-colors disabled:opacity-50"
      title={`Export to ${option.name}`}
    >
      {option.icon}
      <span className="sr-only">Export to {option.name}</span>
    </button>
  );
}
